import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { sendChatMessageStream } from '../../services/chat';
import { getResume, Resume } from '../../services/resume';
import {
  createCareerExperience,
  extractCareerExperiencesFromConversation,
  previewCareerExperiencesFromConversation,
  ProposedCareerExperience,
} from '../../services/careerMemory';
import { VoiceMode } from '../../components/Chat/VoiceMode';
import { saveVoiceTranscript } from '../../services/voiceConversation';
import type { TranscriptEntry } from '../../hooks/useGeminiLive';
import './CareerInterview.css';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  createdAt?: string;
}

interface CareerInterviewProps {
  conversationId?: string | null;
}

interface PendingExperience extends ProposedCareerExperience {
  pendingId: string;
}

export function CareerInterview({ conversationId: initialConversationId }: CareerInterviewProps = {}) {
  const navigate = useNavigate();
  const { setPageCrumbs } = useBreadcrumbs();
  const { t, language } = useLanguage();
  const tRef = useRef(t);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>(initialConversationId || undefined);
  const [error, setError] = useState<string>('');
  const [streamingContent, setStreamingContent] = useState<string>('');
  const [resume, setResume] = useState<Resume | null>(null);
  const [resumeLoading, setResumeLoading] = useState(true);
  const [voiceModeActive, setVoiceModeActive] = useState(false);
  const [loadingPendingQueue, setLoadingPendingQueue] = useState(false);
  const [savingPendingQueue, setSavingPendingQueue] = useState(false);
  const [pendingExperiences, setPendingExperiences] = useState<PendingExperience[]>([]);
  const [autoApproveAll, setAutoApproveAll] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [endingInterview, setEndingInterview] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    setPageCrumbs([{ label: t('careerInterview.title') }]);
  }, [setPageCrumbs, t]);

  useEffect(() => {
    const loadResume = async () => {
      try {
        setResumeLoading(true);
        const resumeData = await getResume();
        setResume(resumeData);
      } catch (err) {
        console.warn('Failed to load resume in CareerInterview:', err);
      } finally {
        setResumeLoading(false);
      }
    };

    loadResume();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent]);

  const getGreeting = (): string => {
    const baseGreeting = tRef.current('careerInterview.greeting');

    if (resume?.data?.personalInfo?.name) {
      const name = resume.data.personalInfo.name;
      const currentRole = resume.data.experience?.[0]?.position;

      return `Hi ${name}! 👋 I can see you've already uploaded your resume - that's great! I see you're working as a ${currentRole || 'professional'}.

Now I'm here to help you dig deeper into your professional experiences beyond what's on the resume. Let's build a comprehensive memory bank of your achievements, challenges, and growth.

I'd love to explore:
• A specific achievement or project you're most proud of
• A challenge you overcame and what you learned
• Your early career experiences that shaped your career path
• Recent accomplishments or wins
• Areas where you want to grow or develop

What sounds interesting to you?`;
    }

    return baseGreeting;
  };

  useEffect(() => {
    if (!initialConversationId && !resumeLoading) {
      setMessages([
        {
          role: 'assistant',
          content: getGreeting(),
          createdAt: new Date().toISOString(),
        },
      ]);
    }
  }, [initialConversationId, resumeLoading]);

  const formatMessageDate = (dateString?: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) {
      return diffMins === 0 ? 'Just now' : `${diffMins} min ago`;
    }

    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setError('');

    setMessages((prev) => [...prev, { role: 'user', content: userMessage, createdAt: new Date().toISOString() }]);

    setLoading(true);
    setStreamingContent('');
    let accumulatedContent = '';

    try {
      await sendChatMessageStream(userMessage, conversationId, {
        onConversationId: (id) => {
          if (!conversationId) {
            setConversationId(id);
          }
        },
        onChunk: (content) => {
          accumulatedContent += content;
          setStreamingContent(accumulatedContent);
        },
        onDone: () => {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: accumulatedContent,
              createdAt: new Date().toISOString(),
            },
          ]);
          setStreamingContent('');
          setLoading(false);
        },
        onError: (errorMsg) => {
          setError(errorMsg);
          setStreamingContent('');
          setLoading(false);
        },
      }, { language, mode: 'careerInterview' });
    } catch (err: any) {
      console.error('Chat error:', err);
      setError(err.message || t('careerInterview.errors.sendMessageDefault'));
      setStreamingContent('');
      setLoading(false);
    }
  };

  const handleVoiceClose = useCallback(async (transcript: TranscriptEntry[]) => {
    setVoiceModeActive(false);
    if (transcript.length === 0) {
      return;
    }

    try {
      const savedId = await saveVoiceTranscript(conversationId, transcript);
      if (!conversationId) {
        setConversationId(savedId);
      }
      const newMessages = transcript.map(entry => ({
        role: entry.role as 'user' | 'assistant',
        content: entry.text,
        createdAt: entry.timestamp,
      }));
      setMessages(prev => [...prev, ...newMessages]);
    } catch (err: any) {
      console.error('Failed to save voice transcript:', err);
      setError(err.message || t('careerInterview.errors.saveVoiceDefault'));
    }
  }, [conversationId, t]);

  const savePendingExperience = useCallback(async (item: PendingExperience) => {
    await createCareerExperience({
      title: item.title,
      description: item.description,
      startDate: item.startDate,
      endDate: item.endDate,
      tags: Array.from(new Set([...(item.skills || []), ...(item.technologies || [])])).slice(0, 10),
    });
  }, []);

  const handleApproveOne = useCallback(async (pendingId: string) => {
    const item = pendingExperiences.find((entry) => entry.pendingId === pendingId);
    if (!item || savingPendingQueue) {
      return;
    }

    setSavingPendingQueue(true);
    setSaveStatus('');
    setError('');

    try {
      await savePendingExperience(item);
      setPendingExperiences((prev) => prev.filter((entry) => entry.pendingId !== pendingId));
      setSaveStatus(t('careerInterview.savedToTimeline').replace('{{count}}', '1'));
    } catch (err: any) {
      console.error('Failed to approve pending experience:', err);
      setError(err.message || t('careerInterview.errors.extractDefault'));
    } finally {
      setSavingPendingQueue(false);
    }
  }, [pendingExperiences, savePendingExperience, savingPendingQueue, t]);

  const handleApproveAll = useCallback(async (itemsToApprove?: PendingExperience[]) => {
    const items = itemsToApprove || pendingExperiences;
    if (!items.length || savingPendingQueue) {
      return;
    }

    setSavingPendingQueue(true);
    setSaveStatus('');
    setError('');

    let savedCount = 0;
    try {
      for (const item of items) {
        await savePendingExperience(item);
        savedCount += 1;
      }
      const approvedIds = new Set(items.map((item) => item.pendingId));
      setPendingExperiences((prev) => prev.filter((entry) => !approvedIds.has(entry.pendingId)));
      setSaveStatus(t('careerInterview.savedToTimeline').replace('{{count}}', String(savedCount)));
    } catch (err: any) {
      console.error('Failed to approve all pending experiences:', err);
      setError(err.message || t('careerInterview.errors.extractDefault'));
    } finally {
      setSavingPendingQueue(false);
    }
  }, [pendingExperiences, savePendingExperience, savingPendingQueue, t]);

  const handleExtractPending = useCallback(async () => {
    if (!conversationId || loadingPendingQueue || savingPendingQueue) {
      return;
    }

    setLoadingPendingQueue(true);
    setSaveStatus('');
    setError('');

    try {
      const result = await previewCareerExperiencesFromConversation(conversationId);
      const mapped: PendingExperience[] = (result.extractedExperiences || []).map((item, index) => ({
        ...item,
        pendingId: `${Date.now()}-${index}`,
      }));

      setPendingExperiences(mapped);

      if (!mapped.length) {
        setSaveStatus(result.message || t('careerInterview.noExperiencesExtracted'));
        return;
      }

      if (autoApproveAll) {
        await handleApproveAll(mapped);
      }
    } catch (err: any) {
      console.error('Failed to extract pending experiences:', err);
      setError(err.message || t('careerInterview.errors.extractDefault'));
    } finally {
      setLoadingPendingQueue(false);
    }
  }, [conversationId, loadingPendingQueue, savingPendingQueue, autoApproveAll, handleApproveAll, t]);

  const handleDismissOne = useCallback((pendingId: string) => {
    if (savingPendingQueue) {
      return;
    }
    setPendingExperiences((prev) => prev.filter((entry) => entry.pendingId !== pendingId));
  }, [savingPendingQueue]);

  const handlePendingChange = useCallback((pendingId: string, field: 'title' | 'description', value: string) => {
    setPendingExperiences((prev) =>
      prev.map((entry) => (entry.pendingId === pendingId ? { ...entry, [field]: value } : entry))
    );
  }, []);

  const handleEndInterview = useCallback(async () => {
    if (endingInterview) {
      return;
    }

    setEndingInterview(true);
    try {
      if (conversationId && messages.length > 2) {
        try {
          await extractCareerExperiencesFromConversation(conversationId);
        } catch (extractErr) {
          console.warn('Career interview auto extraction failed (non-blocking):', extractErr);
        }
      }
      navigate('/dashboard');
    } finally {
      setEndingInterview(false);
    }
  }, [endingInterview, conversationId, messages.length, navigate]);

  return (
    <div className="career-interview-container">
      {voiceModeActive && (
        <VoiceMode
          onClose={handleVoiceClose}
          chatHistory={messages.map(m => ({ role: m.role, content: m.content }))}
        />
      )}

      <div className="career-interview-header">
        <button
          className="btn btn-secondary interview-home-btn"
          onClick={handleEndInterview}
          disabled={endingInterview}
        >
          ← {endingInterview ? t('common.loading') : t('nav.home')}
        </button>
        <h1>{t('careerInterview.title')}</h1>
        <p className="subtitle">{t('careerInterview.subtitle')}</p>
      </div>

      <div className="career-interview-chat">
        <div className="messages-container">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              data-testid="career-message"
              data-role={msg.role}
              className={`message ${msg.role}`}
            >
              {msg.createdAt && (
                <div className="message-timestamp">
                  {formatMessageDate(msg.createdAt)}
                </div>
              )}
              <div className="message-bubble">
                <div className="message-content">{msg.content}</div>
              </div>
            </div>
          ))}

          {streamingContent && (
            <div className="message assistant">
              <div className="message-bubble">
                <div className="message-content">{streamingContent}</div>
              </div>
            </div>
          )}

          {loading && !streamingContent && (
            <div className="message assistant">
              <div className="message-bubble">
                <div className="thinking-indicator">
                  <span>{t('careerInterview.thinking')}</span>
                  <div className="dots">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSubmit} className="input-form">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('careerInterview.placeholder')}
            disabled={loading}
            rows={2}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
          />
          <button
            type="button"
            className="voice-button"
            onClick={() => setVoiceModeActive(true)}
            disabled={loading}
            title={t('voice.startVoiceMode')}
          >
            {'🎤'}
          </button>
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="send-button"
          >
            {t('chat.send')}
          </button>
        </form>
      </div>

      <div className="career-interview-sidebar">
        <div className="info-card">
          <h3>💡 {t('careerInterview.tips.title')}</h3>
          <ul>
            <li>{t('careerInterview.tips.specific')}</li>
            <li>{t('careerInterview.tips.metrics')}</li>
            <li>{t('careerInterview.tips.challenges')}</li>
            <li>{t('careerInterview.tips.impact')}</li>
          </ul>
        </div>

        {conversationId && messages.length > 3 && (
          <div className="info-card">
            <h3>✅ {t('careerInterview.progress.title')}</h3>
            <p>{t('careerInterview.progress.message').replace('{{count}}', String(messages.filter(m => m.role === 'user').length))}</p>
            {saveStatus && <p className="save-status">{saveStatus}</p>}
            <div className="interview-progress-actions">
              <button
                className="btn btn-primary interview-action-button"
                onClick={handleExtractPending}
                disabled={loadingPendingQueue || savingPendingQueue}
                data-testid="career-extract-suggestions"
              >
                {loadingPendingQueue ? t('common.loading') : t('careerInterview.extractSuggestions')}
              </button>
              <button
                className="btn btn-secondary interview-action-button"
                onClick={() => navigate('/career-timeline')}
                disabled={loadingPendingQueue || savingPendingQueue}
              >
                {t('careerInterview.viewTimeline')}
              </button>
            </div>

            <label className="pending-auto-approve-toggle">
              <input
                type="checkbox"
                checked={autoApproveAll}
                onChange={(event) => setAutoApproveAll(event.target.checked)}
                disabled={loadingPendingQueue || savingPendingQueue}
                data-testid="career-auto-approve"
              />
              {t('careerInterview.autoApproveAll')}
            </label>

            {pendingExperiences.length > 0 && (
              <>
                <div className="pending-header-row">
                  <p>{t('careerInterview.pendingCount').replace('{{count}}', String(pendingExperiences.length))}</p>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleApproveAll()}
                    disabled={savingPendingQueue}
                    data-testid="career-approve-all"
                  >
                    {savingPendingQueue ? t('common.loading') : t('careerInterview.approveAll')}
                  </button>
                </div>

                <div className="pending-list">
                  {pendingExperiences.map((item) => (
                    <div key={item.pendingId} className="pending-card" data-testid="career-pending-card">
                      <input
                        className="pending-title"
                        value={item.title}
                        onChange={(event) => handlePendingChange(item.pendingId, 'title', event.target.value)}
                        disabled={savingPendingQueue}
                        title={t('careerInterview.pendingTitleField')}
                        placeholder={t('careerInterview.pendingTitleField')}
                        aria-label={t('careerInterview.pendingTitleField')}
                        data-testid="career-pending-title"
                      />
                      <textarea
                        className="pending-description"
                        value={item.description}
                        onChange={(event) => handlePendingChange(item.pendingId, 'description', event.target.value)}
                        disabled={savingPendingQueue}
                        rows={3}
                        title={t('careerInterview.pendingDescriptionField')}
                        placeholder={t('careerInterview.pendingDescriptionField')}
                        aria-label={t('careerInterview.pendingDescriptionField')}
                        data-testid="career-pending-description"
                      />
                      <div className="pending-card-actions">
                        <button
                          className="btn btn-primary"
                          onClick={() => handleApproveOne(item.pendingId)}
                          disabled={savingPendingQueue || !item.title?.trim()}
                          data-testid="career-approve-item"
                        >
                          {t('careerInterview.approve')}
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleDismissOne(item.pendingId)}
                          disabled={savingPendingQueue}
                          data-testid="career-dismiss-item"
                        >
                          {t('careerInterview.dismiss')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
